import React from "react";
import { Link } from "react-router-dom";
import { differenceInDays, parseISO, isValid } from "date-fns";
import { CallItem } from "../../../types/grants";
import { formatDate } from "../../../utils/date.utils";
import { STATUS_LABELS, STATUS_COLORS, CALL_TYPE_LABELS, getTargetGroupLabel } from "../../../constants/grants.constants";
import { getRegionLabel } from "../../../constants/regions";
import { formatProjectLabel, formatCompetitionLabel, shouldShowUpdatedAt } from "./callsTableUtils";
import { GrantsLabelItem, ArticleLabelMap } from "../../../services/grantsApi";
import CallLabelCell from "../labels/CallLabelCell";
import { CallBarometerData } from "../../../hooks/useBarometerBatch";
import { isSafeUrl } from "../../../utils/url-validation";

// ─── Context passed to each column's render function ───────────────────────

export type RenderContext = {
  isAdmin: boolean;
  hideStatusColumn: boolean;
  hasLabelInteraction: boolean;
  labels: GrantsLabelItem[];
  articleLabels: ArticleLabelMap;
  onSetCallLabel: (callId: string, labelId: string | null) => Promise<unknown>;
  onCreateAndAssign: (name: string, callId: string) => Promise<unknown>;
  onManageLabels: () => void;
  getCallBarometer: ((call: CallItem) => CallBarometerData) | undefined;
  onEdit?: (call: CallItem) => void;
  sortBy?: string | null;
  sortOrder?: "ASC" | "DESC";
  onSort?: (field: string) => void;
};

// ─── Column ID type ─────────────────────────────────────────────────────────

export type ColumnId =
  | "createdAt"
  | "program"
  | "status"
  | "title"
  | "operator"
  | "startDate"
  | "endDate"
  | "allocation"
  | "label"
  | "region"
  | "actions"
  | "callType"
  | "grantType"
  | "fundingRange"
  | "targetGroups"
  | "announcedAt"
  | "officialUrl"
  | "applicationUrl"
  | "programName";

// ─── Column definition ───────────────────────────────────────────────────────

export type ColumnDef = {
  id: ColumnId;
  /** Human-readable label (Polish) shown in panel and header */
  label: string;
  /** Field name for server-side sorting (matches ALLOWED_SORT_FIELDS in backend) */
  sortField?: string;
  /**
   * aria-label użyte na przycisku sortowania w stanie nieaktywnym.
   * Wymagane gdy `label` nie tworzy gramatycznie poprawnej frazy
   * "Sortuj według {label}" (np. "Termin od" → "Sortuj według daty rozpoczęcia").
   * Kontrakt testów E2E (grants-calls-sorting).
   */
  sortAriaLabel?: string;
  align?: "left" | "right" | "center";
  /** Whether this column is visible by default */
  defaultVisible: boolean;
  /**
   * Whether user can toggle/reorder this column in the config panel.
   * contextGated columns (e.g. "actions") are excluded from the panel.
   */
  userConfigurable: boolean;
  /** Render the header cell content (without <th> wrapper) */
  renderHeader: (ctx: RenderContext) => React.ReactNode;
  /** Render the data cell content (without <td> wrapper) */
  render: (call: CallItem, ctx: RenderContext) => React.ReactNode;
  /** CSS classes for <th> */
  thClass?: string;
  /** CSS classes for <td> */
  tdClass?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Gets urgency color class for deadline dates
 */
const getDeadlineColor = (dateStr: string | null, status: string): string => {
  if (status !== "OPEN" || !dateStr) return "text-neutral-900";
  try {
    const date = parseISO(dateStr);
    if (!isValid(date)) return "text-neutral-900";
    const daysLeft = differenceInDays(date, new Date());
    if (daysLeft < 3) return "text-red-600 font-semibold";
    if (daysLeft <= 7) return "text-orange-600";
    return "text-neutral-900";
  } catch {
    return "text-neutral-900";
  }
};

/**
 * Formats allocation as "X mln PLN" or formatted currency
 */
export const formatAllocation = (allocation: number | null): string => {
  if (allocation === null || allocation === undefined) return "-";
  if (allocation >= 1000000) {
    return `${(allocation / 1000000).toFixed(1)} mln PLN`;
  }
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: "code",
  }).format(allocation);
};

/**
 * Formats regions for display, showing first + count
 */
const formatRegions = (regions: string[]): string => {
  if (!regions || regions.length === 0) return "-";
  const label = (r: string) => getRegionLabel(r);
  if (regions.length === 1) return label(regions[0]);
  return `${label(regions[0])} +${regions.length - 1}`;
};

/** Sort icon component */
const SortIcon: React.FC<{
  field: string;
  activeField: string | null | undefined;
  direction: "ASC" | "DESC";
}> = ({ field, activeField, direction }) => {
  if (activeField !== field) {
    return (
      <svg className="w-4 h-4 ml-1 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }
  return direction === "ASC" ? (
    <svg className="w-4 h-4 ml-1 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  ) : (
    <svg className="w-4 h-4 ml-1 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
};

/** Sortable header button */
const SortableHeader: React.FC<{
  field: string;
  label: string;
  inactiveAriaLabel: string;
  ctx: RenderContext;
}> = ({ field, label, inactiveAriaLabel, ctx }) => {
  const isActive = ctx.sortBy === field;
  const dir = ctx.sortOrder ?? "ASC";
  return (
    <button
      onClick={() => ctx.onSort?.(field)}
      className="flex items-center cursor-pointer hover:text-neutral-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
      aria-label={
        isActive
          ? `Sortuj ${dir === "ASC" ? "malejąco" : "rosnąco"}`
          : inactiveAriaLabel
      }
    >
      {label}
      <SortIcon field={field} activeField={ctx.sortBy} direction={dir} />
    </button>
  );
};

// ─── Column registry ─────────────────────────────────────────────────────────

export const COLUMNS_REGISTRY: ColumnDef[] = [
  // ── createdAt ──────────────────────────────────────────────────────────────
  {
    id: "createdAt",
    label: "Dodano",
    sortField: "createdAt",
    sortAriaLabel: "Sortuj według daty dodania",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-28",
    tdClass: "whitespace-nowrap",
    renderHeader: (ctx) => (
      <SortableHeader
        field="createdAt"
        label="Dodano"
        inactiveAriaLabel="Sortuj według daty dodania"
        ctx={ctx}
      />
    ),
    render: (call) => (
      <div>
        <span className="text-sm text-neutral-900">{formatDate(call.createdAt)}</span>
        {shouldShowUpdatedAt(call.createdAt, call.updatedAt) && (
          <div
            className="flex items-center gap-1 mt-0.5"
            title={`Ostatnia aktualizacja: ${formatDate(call.updatedAt)}`}
          >
            <svg
              className="w-3 h-3 text-neutral-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="sr-only">Zaktualizowano: </span>
            <span className="text-xs text-neutral-500">{formatDate(call.updatedAt)}</span>
          </div>
        )}
      </div>
    ),
  },

  // ── program ────────────────────────────────────────────────────────────────
  {
    id: "program",
    label: "Program",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-20",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Program",
    render: (call) => (
      <div>
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
          {call.program?.code || "-"}
        </span>
        {formatCompetitionLabel(call.competition) && (
          <div
            className="text-xs text-neutral-600 truncate max-w-[200px] mt-0.5"
            title={call.competition?.name}
            aria-label={`Konkurs: ${formatCompetitionLabel(call.competition)}`}
          >
            {formatCompetitionLabel(call.competition)}
          </div>
        )}
      </div>
    ),
  },

  // ── status ─────────────────────────────────────────────────────────────────
  {
    id: "status",
    label: "Status",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-24",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Status",
    render: (call) => (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          STATUS_COLORS[call.status] || "bg-neutral-100 text-neutral-800"
        }`}
      >
        {STATUS_LABELS[call.status] || call.status}
      </span>
    ),
  },

  // ── title ──────────────────────────────────────────────────────────────────
  {
    id: "title",
    label: "Tytuł",
    sortField: "title",
    sortAriaLabel: "Sortuj według tytułu",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    renderHeader: (ctx) => (
      <SortableHeader
        field="title"
        label="Tytuł"
        inactiveAriaLabel="Sortuj według tytułu"
        ctx={ctx}
      />
    ),
    render: (call) => (
      <div>
        {formatProjectLabel(call.project) && (
          <div
            className="text-xs text-neutral-600 truncate max-w-[300px] mb-0.5"
            title={call.project?.name}
            aria-label={`Projekt: ${formatProjectLabel(call.project)}`}
          >
            {formatProjectLabel(call.project)}
          </div>
        )}
        <Link
          to={`/grants/calls/${call.id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
        >
          {call.title}
        </Link>
      </div>
    ),
  },

  // ── operator ───────────────────────────────────────────────────────────────
  {
    id: "operator",
    label: "Operator",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-40",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Operator",
    render: (call) => (
      <span
        className="text-sm text-neutral-900 truncate block max-w-[150px]"
        title={call.operator?.name}
      >
        {call.operator?.shortName || call.operator?.name || "-"}
      </span>
    ),
  },

  // ── startDate ──────────────────────────────────────────────────────────────
  {
    id: "startDate",
    label: "Termin od",
    sortField: "startDate",
    sortAriaLabel: "Sortuj według daty rozpoczęcia",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-28",
    tdClass: "whitespace-nowrap",
    renderHeader: (ctx) => (
      <SortableHeader
        field="startDate"
        label="Termin od"
        inactiveAriaLabel="Sortuj według daty rozpoczęcia"
        ctx={ctx}
      />
    ),
    render: (call) => (
      <span className="text-sm text-neutral-900">
        {formatDate(call.applicationStartDate)}
      </span>
    ),
  },

  // ── endDate ────────────────────────────────────────────────────────────────
  {
    id: "endDate",
    label: "Termin do",
    sortField: "endDate",
    sortAriaLabel: "Sortuj według daty zakończenia",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-28",
    tdClass: "whitespace-nowrap",
    renderHeader: (ctx) => (
      <SortableHeader
        field="endDate"
        label="Termin do"
        inactiveAriaLabel="Sortuj według daty zakończenia"
        ctx={ctx}
      />
    ),
    render: (call) => (
      <span
        className={`text-sm ${getDeadlineColor(call.applicationEndDate, call.status)}`}
      >
        {formatDate(call.applicationEndDate)}
      </span>
    ),
  },

  // ── allocation ─────────────────────────────────────────────────────────────
  {
    id: "allocation",
    label: "Alokacja",
    align: "right",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-28",
    tdClass: "whitespace-nowrap text-right",
    renderHeader: () => "Alokacja",
    render: (call) => (
      <span className="text-sm text-neutral-900">
        {formatAllocation(call.allocation)}
      </span>
    ),
  },

  // ── label ──────────────────────────────────────────────────────────────────
  {
    id: "label",
    label: "Etykieta",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-32",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Etykieta",
    render: (call, ctx) =>
      ctx.hasLabelInteraction ? (
        <CallLabelCell
          callId={call.id}
          labels={ctx.labels}
          currentLabel={ctx.articleLabels?.[call.id] || null}
          onSetLabel={ctx.onSetCallLabel}
          onCreateAndAssign={ctx.onCreateAndAssign}
          onManageLabels={ctx.onManageLabels}
        />
      ) : (
        <span className="text-sm text-neutral-400">&mdash;</span>
      ),
  },

  // ── region ─────────────────────────────────────────────────────────────────
  {
    id: "region",
    label: "Region",
    align: "left",
    defaultVisible: true,
    userConfigurable: true,
    thClass: "w-28",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Region",
    render: (call, ctx) => (
      <div className="flex items-center gap-1">
        <span
          className="text-sm text-neutral-900"
          title={call.targetRegions?.map((r) => getRegionLabel(r)).join(", ")}
        >
          {formatRegions(call.targetRegions)}
        </span>
        {/* Barometer icon rendered by CallsTable via separate wrapper (needs ref) */}
      </div>
    ),
  },

  // ── actions (contextGated — admin only, always last, not user-configurable) ─
  {
    id: "actions",
    label: "Akcje",
    align: "right",
    defaultVisible: true,
    userConfigurable: false,
    thClass: "w-12",
    tdClass: "whitespace-nowrap text-right",
    renderHeader: () => "Akcje",
    render: (call, ctx) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          ctx.onEdit?.(call);
        }}
        className="text-neutral-400 hover:text-neutral-600"
        title="Edytuj"
        aria-label={`Edytuj nabór ${call.title}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
          />
        </svg>
      </button>
    ),
  },

  // ─── New columns (defaultVisible: false) ──────────────────────────────────

  // ── callType ───────────────────────────────────────────────────────────────
  {
    id: "callType",
    label: "Typ naboru",
    align: "left",
    defaultVisible: false,
    userConfigurable: true,
    thClass: "w-32",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Typ naboru",
    render: (call) => (
      <span className="text-sm text-neutral-900">
        {CALL_TYPE_LABELS[call.callType] || call.callType || "-"}
      </span>
    ),
  },

  // ── grantType ──────────────────────────────────────────────────────────────
  {
    id: "grantType",
    label: "Rodzaj wsparcia",
    align: "left",
    defaultVisible: false,
    userConfigurable: true,
    thClass: "w-36",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Rodzaj wsparcia",
    render: (call) => (
      <span className="text-sm text-neutral-900">
        {call.grantType || "-"}
      </span>
    ),
  },

  // ── fundingRange ────────────────────────────────────────────────────────────
  {
    id: "fundingRange",
    label: "Zakres finansowania",
    align: "right",
    defaultVisible: false,
    userConfigurable: true,
    thClass: "w-40",
    tdClass: "whitespace-nowrap text-right",
    renderHeader: () => "Zakres finansowania",
    render: (call) => {
      if (!call.fundingRange) return <span className="text-sm text-neutral-900">-</span>;
      const { min, max } = call.fundingRange;
      if (!min && !max) return <span className="text-sm text-neutral-900">-</span>;
      const parts: string[] = [];
      if (min) parts.push(formatAllocation(min));
      if (max) parts.push(formatAllocation(max));
      return (
        <span className="text-sm text-neutral-900">
          {parts.join(" – ")}
        </span>
      );
    },
  },

  // ── targetGroups ───────────────────────────────────────────────────────────
  {
    id: "targetGroups",
    label: "Grupy docelowe",
    align: "left",
    defaultVisible: false,
    userConfigurable: true,
    thClass: "w-40",
    tdClass: "",
    renderHeader: () => "Grupy docelowe",
    render: (call) => {
      if (!call.targetGroups || call.targetGroups.length === 0) {
        return <span className="text-sm text-neutral-900">-</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {call.targetGroups.map((tg) => (
            <span
              key={tg}
              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-neutral-100 text-neutral-700"
            >
              {getTargetGroupLabel(tg)}
            </span>
          ))}
        </div>
      );
    },
  },

  // ── announcedAt ────────────────────────────────────────────────────────────
  {
    id: "announcedAt",
    label: "Ogłoszono",
    align: "left",
    defaultVisible: false,
    userConfigurable: true,
    thClass: "w-28",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Ogłoszono",
    render: (call) => (
      <span className="text-sm text-neutral-900">
        {formatDate(call.announcedAt)}
      </span>
    ),
  },

  // ── officialUrl ────────────────────────────────────────────────────────────
  {
    id: "officialUrl",
    label: "Strona naboru",
    align: "left",
    defaultVisible: false,
    userConfigurable: true,
    thClass: "w-32",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Strona naboru",
    render: (call) =>
      isSafeUrl(call.officialUrl ?? "", { requireHttps: true }) ? (
        <a
          href={call.officialUrl!}
          rel="noopener noreferrer"
          target="_blank"
          onClick={(e) => e.stopPropagation()}
          className="text-sm text-primary-600 hover:text-primary-800 hover:underline truncate block max-w-[150px]"
        >
          Link
        </a>
      ) : (
        <span className="text-sm text-neutral-400">-</span>
      ),
  },

  // ── applicationUrl ─────────────────────────────────────────────────────────
  {
    id: "applicationUrl",
    label: "Link aplikacji",
    align: "left",
    defaultVisible: false,
    userConfigurable: true,
    thClass: "w-32",
    tdClass: "whitespace-nowrap",
    renderHeader: () => "Link aplikacji",
    render: (call) =>
      isSafeUrl(call.applicationUrl ?? "", { requireHttps: true }) ? (
        <a
          href={call.applicationUrl!}
          rel="noopener noreferrer"
          target="_blank"
          onClick={(e) => e.stopPropagation()}
          className="text-sm text-primary-600 hover:text-primary-800 hover:underline truncate block max-w-[150px]"
        >
          Link
        </a>
      ) : (
        <span className="text-sm text-neutral-400">-</span>
      ),
  },

  // ── programName ────────────────────────────────────────────────────────────
  {
    id: "programName",
    label: "Nazwa programu",
    align: "left",
    defaultVisible: false,
    userConfigurable: true,
    thClass: "w-40",
    tdClass: "",
    renderHeader: () => "Nazwa programu",
    render: (call) => (
      <span
        className="text-sm text-neutral-900 truncate block max-w-[200px]"
        title={call.program?.name}
      >
        {call.program?.name || "-"}
      </span>
    ),
  },
];

// ─── Default column order (only visible columns, in display order) ────────────

/**
 * Default column order — IDs of columns visible by default, in order.
 * This is the single source of truth for the default layout.
 * Must match the original CallsTable layout exactly (visual regression guard).
 */
export const DEFAULT_COLUMN_ORDER: ColumnId[] = [
  "createdAt",
  "program",
  "status",
  "title",
  "operator",
  "startDate",
  "endDate",
  "allocation",
  "label",
  "region",
];

/**
 * Whitelist of all known column IDs for safe deserialization.
 * Used in useCallsColumnConfig to filter out stale/unknown IDs from localStorage.
 */
export const KNOWN_COLUMN_IDS = new Set<ColumnId>(
  COLUMNS_REGISTRY.map((c) => c.id)
);

/**
 * Helper: check if a string is a known column ID
 */
export function isKnownColumnId(id: string): id is ColumnId {
  return KNOWN_COLUMN_IDS.has(id as ColumnId);
}
